import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Box, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { UserRole } from '../../types/rbac';
import { Icon } from '../../components/participant/Icon';
import { participantColors as c, fadeUpKeyframes } from '../../theme/participantTheme';
import logo from '../../logo.png';

interface LoginFormData {
  cpfOrEmail: string;
  password: string;
}

const loginSchema = yup.object().shape({
  cpfOrEmail: yup.string().required('Digite o seu CPF para entrar'),
  password: yup.string().required('Digite a sua senha'),
});

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: { cpfOrEmail: '', password: '' },
  });

  const fieldError = errors.cpfOrEmail?.message || errors.password?.message || '';
  const errorMessage = authError || fieldError;

  const onSubmit: SubmitHandler<LoginFormData> = async (data) => {
    setLoading(true);
    setAuthError('');
    try {
      const response = await login({
        cpfOrEmail: data.cpfOrEmail,
        password: data.password,
        role: UserRole.USUARIO_FINAL,
      });

      const user = response.user;

      switch (user.role) {
        case 'SUPER_ADMIN':
          showSnackbar('Bem-vindo, Administrador!', 'success');
          navigate('/dashboard');
          break;
        case 'ASSOCIACAO':
          showSnackbar('Login realizado com sucesso!', 'success');
          navigate('/dashboard');
          break;
        case 'USUARIO_FINAL':
          navigate('/dashboard');
          break;
        default:
          navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Erro no login:', error);

      if (error.response?.status === 401) {
        const backendMessage = error.response?.data?.message;
        if (backendMessage?.includes('Usuário não encontrado')) {
          setAuthError('CPF/email não cadastrado no sistema.');
        } else if (backendMessage?.includes('Senha inválida')) {
          setAuthError('Senha incorreta. Verifique e tente novamente.');
        } else {
          setAuthError('CPF/email ou senha incorretos.');
        }
      } else if (error.response?.status === 403) {
        setAuthError('Acesso negado. Verifique suas permissões.');
      } else if (error.response?.status === 429) {
        setAuthError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else if (error.message) {
        setAuthError(error.message);
      } else {
        setAuthError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: c.gradients.pageBg,
        fontFamily: c.fontFamily,
        color: c.textBase,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px 48px 16px',
      }}
    >
      <style>{fadeUpKeyframes}</style>

      <Box
        sx={{
          width: '100%',
          maxWidth: 460,
          display: 'flex',
          flexDirection: 'column',
          gap: '18px',
          animation: 'participantFadeUp 0.4s ease both',
        }}
      >
        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', pb: '4px' }}>
          <Box component="img" src={logo} alt="ACIAG" sx={{ height: 68, width: 'auto', objectFit: 'contain' }} />
        </Box>

        {/* Card */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            background: c.surface,
            borderRadius: `${c.radius.cardLarge}px`,
            padding: '28px 24px',
            boxShadow: c.shadows.loginCard,
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
          }}
        >
          <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Box component="div" sx={{ fontSize: 27, fontWeight: 800, color: c.textDarkest, lineHeight: 1.15 }}>
              Bem-vindo!
            </Box>
            <Box component="div" sx={{ fontSize: 16, color: c.textSecondary, lineHeight: 1.45 }}>
              Entre para cadastrar seus cupons e concorrer aos prêmios
            </Box>
          </Box>

          {/* CPF/Email */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box component="label" sx={{ fontSize: 15.5, fontWeight: 800, color: c.textDarkest }}>
              CPF ou email
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                border: `2px solid ${errors.cpfOrEmail ? c.error : c.border}`,
                borderRadius: `${c.radius.input}px`,
                background: c.surface,
                pl: '14px',
              }}
            >
              <Icon name="badge" size={22} color={c.brandIcon} />
              <Box
                component="input"
                {...register('cpfOrEmail')}
                placeholder="000.000.000-00"
                autoComplete="username"
                disabled={loading}
                sx={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontFamily: c.fontFamily,
                  fontSize: 17.5,
                  fontWeight: 600,
                  color: c.textBase,
                  padding: '16px 14px 16px 8px',
                  minWidth: 0,
                }}
              />
            </Box>
          </Box>

          {/* Senha */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <Box component="label" sx={{ fontSize: 15.5, fontWeight: 800, color: c.textDarkest }}>
              Senha
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                border: `2px solid ${errors.password ? c.error : c.border}`,
                borderRadius: `${c.radius.input}px`,
                background: c.surface,
                padding: '0 6px 0 14px',
              }}
            >
              <Icon name="lock" size={22} color={c.brandIcon} />
              <Box
                component="input"
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                disabled={loading}
                sx={{
                  flex: 1,
                  border: 'none',
                  background: 'transparent',
                  outline: 'none',
                  fontFamily: c.fontFamily,
                  fontSize: 17.5,
                  fontWeight: 600,
                  color: c.textBase,
                  padding: '16px 8px',
                  minWidth: 0,
                }}
              />
              <Box
                component="button"
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                title="Mostrar/esconder senha"
                sx={{
                  width: 44,
                  height: 44,
                  border: 'none',
                  borderRadius: '10px',
                  background: 'transparent',
                  color: c.brandIcon,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': { background: c.surfaceHover },
                }}
              >
                <Icon name={showPassword ? 'visibility_off' : 'visibility'} size={22} />
              </Box>
            </Box>
            <Box sx={{ textAlign: 'right', mt: '2px' }}>
              <Box
                component="a"
                href="/forgot-password"
                sx={{ fontSize: 15, fontWeight: 800, color: c.brandDark, textDecoration: 'none' }}
              >
                Esqueci minha senha
              </Box>
            </Box>
          </Box>

          {errorMessage && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: c.errorBg,
                border: `2px solid ${c.errorBorder}`,
                borderRadius: `${c.radius.input}px`,
                padding: '13px 15px',
              }}
            >
              <Icon name="error" size={22} color={c.error} />
              <Box component="span" sx={{ fontSize: 15, fontWeight: 700, color: c.errorTextDark, lineHeight: 1.4 }}>
                {errorMessage}
              </Box>
            </Box>
          )}

          <Box
            component="button"
            type="submit"
            disabled={loading}
            sx={{
              width: '100%',
              minHeight: 62,
              border: 'none',
              borderRadius: `${c.radius.button}px`,
              background: c.gradients.primaryButton,
              color: c.onDarkPrimary,
              fontFamily: c.fontFamily,
              fontSize: 19,
              fontWeight: 800,
              cursor: loading ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              boxShadow: c.shadows.primaryButton,
              transition: 'transform 0.15s',
              opacity: loading ? 0.85 : 1,
              '&:hover': loading ? {} : { transform: 'translateY(-2px)' },
            }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: c.onDarkPrimary }} />
            ) : (
              <>
                <Icon name="login" size={25} />
                Entrar
              </>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Box sx={{ flex: 1, height: '1px', background: c.border }} />
            <Box sx={{ fontSize: 14, fontWeight: 700, color: c.textTertiary }}>Primeira vez aqui?</Box>
            <Box sx={{ flex: 1, height: '1px', background: c.border }} />
          </Box>

          <Box
            component="button"
            type="button"
            onClick={() => navigate('/register')}
            disabled={loading}
            sx={{
              width: '100%',
              minHeight: 58,
              border: `2px solid ${c.borderHover}`,
              borderRadius: `${c.radius.button}px`,
              background: c.surface,
              color: c.brandDark,
              fontFamily: c.fontFamily,
              fontSize: 18,
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              '&:hover': { background: c.surfaceHover },
            }}
          >
            <Icon name="person_add" size={24} />
            Criar minha conta
          </Box>
        </Box>

        <Box sx={{ textAlign: 'center', fontSize: 13.5, color: c.textTertiary, fontWeight: 600 }}>
          Sistema de Sorteios — Associação Comercial © 2026
        </Box>
      </Box>
    </Box>
  );
};

export default Login;
